/**
 * Header of all pages of the website.
 */

/**
 * TODO: Heading "Helping Job Seekers" should be replaced with earnings post login.
 */

import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  surveyCompletedState,
  surveySavingAlertState,
} from "_state/atoms/AppAtoms";
import { ModalTypes } from "config/types/modals";
import { useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import useAuth from "../../../hooks/useAuth";
import useModal from "../../../hooks/useModal";
import { deepFreeze } from "../../helpers/miscellaneous";
import { SecondaryButton } from "../atoms/HJSButton";
import HeaderLink from "../atoms/HeaderLink";
import { useGetSurveysInfoQuery, useGetUserProfileQuery } from "queries";
import { maxCompensation } from "config/Consts";

const HeaderLinks: {
  [x: string]: {
    to: string;
    location: string;
    label: string;
    width: string;
    protected: boolean;
  };
} = deepFreeze({
  surveys: {
    to: "/#surveys",
    location: "/",
    label: "Surveys",
    width: "7rem",
    protected: false,
  },
  jobsSearch: {
    to: "/jobs/search",
    location: "/jobs/search",
    label: "Job Search",
    width: "9rem",
    protected: true,
  },
  jobsSaved: {
    to: "/jobs/saved",
    location: "/jobs/saved",
    label: "Saved Jobs",
    width: "9rem",
    protected: true,
  },
  jobsApplied: {
    to: "/jobs/applied",
    location: "/jobs/applied",
    label: "Applied Jobs",
    width: "11rem",
    protected: true,
  },
});

const Header: () => JSX.Element = () => {
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated, userDetails, isUnlockedServices } = useAuth();

  const [alert, setAlert] = useRecoilState(surveySavingAlertState);
  const isSurveyCompleted = useRecoilValue(surveyCompletedState);

  const { onOpen, changeModalType } = useModal();
  const { isOpen, onOpen: openMobileNav, onClose } = useDisclosure();

  const { data: surveysInfo } = useGetSurveysInfoQuery();
  const { data: authMe } = useGetUserProfileQuery();

  const navBtnHandler = (): void => {
    if (
      location?.pathname?.includes("survey") &&
      !location?.pathname?.includes("eligiblity") &&
      !isSurveyCompleted
    ) {
      setAlert((prev) => {
        return {
          isOpen: !prev.isOpen,
        };
      });
      return;
    } else if (isAuthenticated && Boolean(userDetails?.name)) {
      changeModalType(ModalTypes.USER_PROFILE);
    } else {
      changeModalType(ModalTypes.LOGIN);
    }
    onOpen();
  };

  const isMobileNav = useBreakpointValue({
    base: true,
    md: false,
  });

  useEffect(() => {
    if (
      (userDetails as boolean) &&
      isAuthenticated &&
      userDetails?.is_email_verified === false
    ) {
      changeModalType(ModalTypes.VERIFICATION_CODE);
      onOpen();
    }
  }, [isAuthenticated, userDetails]);

  // redirect user to homepage if services are not unlocked
  useEffect(() => {
    if (
      (surveysInfo as boolean) &&
      !(surveysInfo?.[0]?.isCompleted as boolean) &&
      location.pathname.includes("/job")
    )
      history.replace("/");
  }, [surveysInfo, location?.pathname]);

  return (
    <>
      <Drawer placement="left" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Link to="/">
              {(userDetails?.isEligible as boolean)
                ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  `Earnings $${authMe?.amountEarned} out of $${maxCompensation}`
                : "Helping Job Seekers"}
            </Link>
          </DrawerHeader>
          <DrawerBody>
            {Object.keys(HeaderLinks).map((headerLinkKey) => (
              <Flex flexDir="column" key={headerLinkKey}>
                <HeaderLink
                  as={Link}
                  to={HeaderLinks[headerLinkKey].to}
                  fontSize="md"
                  fontWeight="600"
                  w={HeaderLinks[headerLinkKey].width}
                  className={`${
                    HeaderLinks[headerLinkKey].protected
                      ? isAuthenticated && isUnlockedServices
                        ? ""
                        : "disable-link"
                      : ""
                  }
                     !text-start `}
                  pl="initial"
                  onClick={onClose}
                >
                  {HeaderLinks[headerLinkKey].label}
                </HeaderLink>
                <Box
                  className={`animate-width ${
                    location.pathname === HeaderLinks[headerLinkKey].location
                      ? ""
                      : "hidden"
                  }`}
                  height="6px"
                  w="full"
                  bgGradient="linear(to-r, yellow.500, teal.600)"
                  rounded="full"
                ></Box>
              </Flex>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {isMobileNav === true ? (
        <>
          <Flex
            px={[4, 4, 20, 32]}
            justifyContent="space-between"
            alignItems="flex-start"
            py={4}
            top={0}
            position="sticky"
            background="white"
            zIndex={10}
            boxShadow="rgb(99 99 99 / 20%) 0px 2px 8px 0px"
          >
            <Button
              colorScheme="white"
              onClick={openMobileNav}
              px="intitial"
              className="flex flex-col"
            >
              <Flex mb={1} alignItems="center">
                <HamburgerIcon />
                <Text ml={2} mr={0.5}>
                  {Object.keys(HeaderLinks).map((key) =>
                    HeaderLinks[key]?.location === location.pathname
                      ? HeaderLinks[key]?.label
                      : null
                  )}
                </Text>
              </Flex>
              <Box
                key={location.pathname}
                className={`animate-width`}
                height="6px"
                w="full"
                bgGradient="linear(to-r, yellow.500, teal.600)"
                rounded="full"
              ></Box>
            </Button>

            {/* <Button
              background="teal.600"
              color="white"
              onClick={openMobileNav}
              px={7}
              py={1}
              fontSize={14}
            >
              Sign in
            </Button> */}
            {!isAuthenticated ? (
              <SecondaryButton px={7} fontSize={14} onClick={onOpen}>
                Sign in
              </SecondaryButton>
            ) : (
              <Button
                fontWeight="600"
                color="hjsBlack"
                fontSize={14}
                onClick={navBtnHandler}
                px="2rem !important"
                py={3}
                variant="link"
                background={
                  location?.pathname?.includes("survey") &&
                  !location?.pathname?.includes("eligiblity") &&
                  !isSurveyCompleted
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent"
                }
              >
                {location?.pathname?.includes("survey") &&
                !location?.pathname?.includes("eligiblity") &&
                !isSurveyCompleted
                  ? "Save and Quit"
                  : userDetails?.name}
              </Button>
            )}
          </Flex>
        </>
      ) : (
        <Flex
          w="100vw"
          px={{ md: 4, lg: 12 }}
          py={4}
          top={0}
          position="sticky"
          justifyContent="space-between"
          alignItems="center"
          background="white"
          boxShadow="rgb(99 99 99 / 20%) 0px -2px 17px 0px"
          zIndex={10}
        >
          <Link to="/">
            <Heading
              as="h1"
              color="teal.600"
              fontWeight="600"
              /* fontSize="2xl" */ className="font-size-md"
            >
              {(userDetails?.isEligible as boolean)
                ? `Earnings $${
                    userDetails?.amountEarned as number
                  } out of $${maxCompensation}`
                : "Helping Job Seekers"}
            </Heading>
          </Link>
          <Flex
            height="3rem"
            flexGrow={1}
            justifyContent="center"
            alignItems="flex-start"
          >
            {Object.keys(HeaderLinks).map((headerLinkKey) => (
              <Flex flexDir="column" key={headerLinkKey}>
                <HeaderLink
                  as={Link}
                  to={HeaderLinks[headerLinkKey].to}
                  fontSize="md"
                  fontWeight="600"
                  w={HeaderLinks[headerLinkKey].width}
                  className={
                    HeaderLinks[headerLinkKey].protected
                      ? isAuthenticated && isUnlockedServices
                        ? ""
                        : "disable-link"
                      : ""
                  }
                >
                  {HeaderLinks[headerLinkKey].label}
                </HeaderLink>
                <Box
                  className={`animate-width ${
                    location.pathname === HeaderLinks[headerLinkKey].location
                      ? ""
                      : "hidden"
                  }`}
                  height="6px"
                  w="full"
                  bgGradient="linear(to-r, yellow.500, teal.600)"
                  rounded="full"
                ></Box>
              </Flex>
            ))}
          </Flex>
          {!isAuthenticated ? (
            <SecondaryButton px={8} onClick={onOpen}>
              Sign in
            </SecondaryButton>
          ) : (
            <Button
              fontWeight="600"
              color="#2D3748"
              fontSize={14}
              onClick={navBtnHandler}
              px="2rem !important"
              mb={2}
              py={3}
              variant="link"
              background={
                location?.pathname?.includes("survey") &&
                !location?.pathname?.includes("eligiblity") &&
                !isSurveyCompleted
                  ? "rgba(0, 0, 0, 0.04)"
                  : "transparent"
              }
              border={
                location?.pathname?.includes("survey") &&
                !location?.pathname?.includes("eligiblity") &&
                !isSurveyCompleted
                  ? "2px solid #285E61"
                  : "initial"
              }
              borderRadius="11px"
            >
              {location?.pathname?.includes("survey") &&
              !location?.pathname?.includes("eligiblity") &&
              !isSurveyCompleted
                ? "Save and Quit"
                : userDetails?.name?.split(" ")?.[0]}
            </Button>
          )}
        </Flex>
      )}
    </>
  );
};

export default Header;
